import React, { useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { PrivacyBody, PRIVACY_LAST_UPDATED } from "@/pages/Privacy";
import { ShieldCheck, LogOut } from "lucide-react";

export default function PrivacyGate({ children }) {
  const { user, checkAuth, logout } = useApp();
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);

  // If not logged in, or already accepted → pass through
  if (!user || user.privacy_accepted) return children;

  const accept = async () => {
    if (!checked) return;
    setSaving(true);
    try {
      await api.post("/auth/accept-privacy");
      await checkAuth();
      toast.success("¡Bienvenido a FaithCycle!");
    } catch {
      toast.error("No se pudo guardar. Intenta de nuevo.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[var(--bg)] overflow-y-auto" data-testid="privacy-gate">
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-10 md:py-16">
        <div className="flex items-center gap-3 mb-6">
          <ShieldCheck className="w-6 h-6 text-[var(--accent)]" strokeWidth={1.5} />
          <p className="uppercase tracking-[0.3em] text-xs text-[var(--text-secondary)]">
            Un paso antes de continuar
          </p>
        </div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[var(--text-primary)]">
          Bienvenido, {user.name?.split(" ")[0] || "amigo"}
        </h1>
        <p className="mt-4 text-[var(--text-secondary)] leading-relaxed">
          Antes de entrar a FaithCycle, por favor lee y acepta nuestra Política de Privacidad y normas de la comunidad.
          Esto nos ayuda a mantener un espacio seguro y respetuoso para todos.
        </p>
        <p className="mt-2 text-xs text-[var(--text-secondary)]">
          Última actualización: {PRIVACY_LAST_UPDATED}
        </p>

        {/* Scrollable policy content */}
        <div
          data-testid="privacy-body"
          className="mt-8 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-6 md:p-8 max-h-[45vh] overflow-y-auto space-y-3 font-serif-body leading-relaxed"
        >
          <PrivacyBody />
        </div>

        {/* Sticky bottom controls */}
        <div className="mt-6 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-5 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="privacy-checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-1 w-5 h-5 accent-[var(--accent)] cursor-pointer"
            />
            <span className="text-sm text-[var(--text-primary)] leading-relaxed">
              He leído y acepto la <strong>Política de Privacidad</strong> y las <strong>normas de conducta</strong> de FaithCycle.
              Entiendo que el incumplimiento de estas normas podrá resultar en el bloqueo permanente de mi cuenta.
            </span>
          </label>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="privacy-accept-btn"
              onClick={accept}
              disabled={!checked || saving}
              className="flex-1 min-w-[200px] h-12 rounded-full bg-[var(--accent)] text-white uppercase tracking-widest text-sm hover:bg-[var(--accent)]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {saving ? "Guardando…" : "Acepto y continuar"}
            </button>
            <button
              data-testid="privacy-decline-btn"
              onClick={logout}
              className="h-12 px-6 rounded-full border border-[var(--border)] uppercase tracking-widest text-sm hover:border-[var(--accent)] transition-colors duration-200 flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
              No acepto
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
